import { Link } from '@inertiajs/react'
import { useState, useMemo } from 'react'
import { Layout } from '@/components/Layout'
import { PageHead } from '@/components/PageHead'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { GitBranch, Clock, CheckCircle, Search, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'

interface Workflow {
  id: string
  external_workflow_id: string
  name: string
  is_active: boolean
  client_name: string
  instance_name: string
  instance_id: string
  client_id: string
  executions_count: number
  success_rate: number
  failure_count: number
  time_saved_minutes: number
  minutes_saved_per_success: number
  minutes_saved_per_failure: number
}

interface Client {
  id: string
  name: string
}

interface Instance {
  id: string
  name: string
  client_id: string
}

interface Props {
  workflows: Workflow[]
  clients: Client[]
  instances: Instance[]
}

type SortKey = 'name' | 'client_name' | 'is_active' | 'executions_count' | 'success_rate' | 'failure_count' | 'time_saved_minutes'
type SortDirection = 'asc' | 'desc'
type StatusFilter = 'all' | 'active' | 'inactive'

function formatTimeSaved(minutes: number): string {
  if (minutes >= 60) {
    return `${(minutes / 60).toFixed(1)}h`
  }
  return `${minutes}m`
}

export default function WorkflowsIndex({ workflows, clients, instances }: Props) {
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)

  // Filter states
  const [clientFilter, setClientFilter] = useState<string | null>(null)
  const [instanceFilter, setInstanceFilter] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  // Get instances filtered by selected client
  const filteredInstances = useMemo(() => {
    if (!clientFilter) return instances
    return instances.filter(i => i.client_id === clientFilter)
  }, [instances, clientFilter])

  // Reset instance filter when client changes
  const handleClientFilterChange = (value: string | null) => {
    setClientFilter(value)
    setInstanceFilter(null)
    setCurrentPage(1)
  }

  const handleInstanceFilterChange = (value: string | null) => {
    setInstanceFilter(value)
    setCurrentPage(1)
  }

  const handleStatusFilterChange = (value: StatusFilter) => {
    setStatusFilter(value)
    setCurrentPage(1)
  }

  const clearFilters = () => {
    setClientFilter(null)
    setInstanceFilter(null)
    setStatusFilter('all')
    setSearch('')
    setCurrentPage(1)
  }

  const hasActiveFilters = clientFilter || instanceFilter || statusFilter !== 'all' || search.trim()

  // Filter
  const filteredWorkflows = useMemo(() => {
    let filtered = workflows

    // Client filter
    if (clientFilter) {
      filtered = filtered.filter(w => w.client_id === clientFilter)
    }

    // Instance filter
    if (instanceFilter) {
      filtered = filtered.filter(w => w.instance_id === instanceFilter)
    }

    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(w => w.is_active)
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(w => !w.is_active)
    }

    // Search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase()
      filtered = filtered.filter(w =>
        w.name.toLowerCase().includes(searchLower) ||
        w.client_name.toLowerCase().includes(searchLower) ||
        w.instance_name.toLowerCase().includes(searchLower) ||
        w.external_workflow_id.toLowerCase().includes(searchLower)
      )
    }

    return filtered
  }, [workflows, clientFilter, instanceFilter, statusFilter, search])

  // Sort
  const sortedWorkflows = useMemo(() => {
    if (!sortKey) return filteredWorkflows
    return [...filteredWorkflows].sort((a, b) => {
      let aVal: string | number | boolean = a[sortKey]
      let bVal: string | number | boolean = b[sortKey]
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1
      if (typeof aVal === 'boolean') {
        aVal = aVal ? 1 : 0
        bVal = bVal ? 1 : 0
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }
      return sortDirection === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [filteredWorkflows, sortKey, sortDirection])

  // Paginate
  const totalPages = Math.ceil(sortedWorkflows.length / itemsPerPage)
  const paginatedWorkflows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    return sortedWorkflows.slice(start, start + itemsPerPage)
  }, [sortedWorkflows, currentPage, itemsPerPage])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  const handleSearch = (value: string) => {
    setSearch(value)
    setCurrentPage(1)
  }

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
    return sortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
  }

  const totalTimeSaved = workflows.reduce((sum, w) => sum + w.time_saved_minutes, 0)
  const activeWorkflows = workflows.filter(w => w.is_active).length

  return (
    <Layout>
      <PageHead title="Workflows" description="View all workflows synced from n8n instances." />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Workflows</h1>
            <p className="text-muted-foreground">
              All workflows synced from n8n instances.
            </p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-4 pt-6">
              <GitBranch className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">{workflows.length}</p>
                <p className="text-sm text-muted-foreground">Total Workflows</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 pt-6">
              <CheckCircle className="h-8 w-8 text-success" />
              <div>
                <p className="text-2xl font-bold">{activeWorkflows}</p>
                <p className="text-sm text-muted-foreground">Active Workflows</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 pt-6">
              <Clock className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">{formatTimeSaved(totalTimeSaved)}</p>
                <p className="text-sm text-muted-foreground">Time Saved (7 days)</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {workflows.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <GitBranch className="h-12 w-12 text-muted-foreground" />
              <h2 className="mt-4 text-xl font-semibold">No workflows yet</h2>
              <p className="mt-2 text-center text-muted-foreground">
                Workflows will appear here after syncing n8n instances.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search workflows..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Select
                value={clientFilter || 'all'}
                onValueChange={(value) => handleClientFilterChange(value === 'all' ? null : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={instanceFilter || 'all'}
                onValueChange={(value) => handleInstanceFilterChange(value === 'all' ? null : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Instances" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Instances</SelectItem>
                  {filteredInstances.map((instance) => (
                    <SelectItem key={instance.id} value={instance.id}>
                      {instance.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={statusFilter}
                onValueChange={(value) => handleStatusFilterChange(value as StatusFilter)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="mr-1 h-4 w-4" />
                  Clear filters
                </Button>
              )}
              <div className="ml-auto">
                <Select
                  value={String(itemsPerPage)}
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value))
                    setCurrentPage(1)
                  }}
                >
                  <SelectTrigger className="w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="25">25 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button onClick={() => handleSort('name')} className="flex items-center font-medium hover:text-foreground">
                        Workflow <SortIcon column="name" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('client_name')} className="flex items-center font-medium hover:text-foreground">
                        Client / Instance <SortIcon column="client_name" />
                      </button>
                    </TableHead>
                    <TableHead className="text-center">
                      <button onClick={() => handleSort('is_active')} className="flex items-center justify-center font-medium hover:text-foreground w-full">
                        Status <SortIcon column="is_active" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleSort('executions_count')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                        Executions <SortIcon column="executions_count" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleSort('success_rate')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                        Success Rate <SortIcon column="success_rate" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleSort('time_saved_minutes')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                        Time Saved <SortIcon column="time_saved_minutes" />
                      </button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedWorkflows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                        No workflows match your search
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedWorkflows.map((workflow) => (
                      <TableRow key={workflow.id}>
                        <TableCell>
                          <Link
                            href={`/workflows/${workflow.id}`}
                            className="font-medium hover:underline"
                          >
                            {workflow.name}
                          </Link>
                          <p className="text-xs text-muted-foreground">
                            ID: {workflow.external_workflow_id}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Link
                            href={`/clients/${workflow.client_id}`}
                            className="hover:underline"
                          >
                            {workflow.client_name}
                          </Link>
                          <span className="text-muted-foreground"> / </span>
                          <Link
                            href={`/n8n_instances/${workflow.instance_id}`}
                            className="hover:underline"
                          >
                            {workflow.instance_name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-center">
                          {workflow.is_active ? (
                            <Badge variant="success">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {workflow.executions_count}
                          {workflow.failure_count > 0 && (
                            <span className="ml-1 text-destructive">
                              ({workflow.failure_count} failed)
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={workflow.success_rate < 80 ? 'text-destructive' : ''}>
                            {workflow.success_rate}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {workflow.time_saved_minutes > 0 ? (
                            <span className="font-medium">{formatTimeSaved(workflow.time_saved_minutes)}</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedWorkflows.length)} of {sortedWorkflows.length} workflows
                  {hasActiveFilters && ` (filtered from ${workflows.length})`}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}
